/**
 * Block attributes (OdDbAttribute) — the editable texts carried by a block reference.
 *
 * Attributes are a different mechanism from dynamic block properties: they are real
 * text entities owned by the reference, addressed by tag (e.g. "DIRECTION").
 *
 * The photomarker helpers in photomarker/transform.ts only ever read the *first*
 * attribute, which is enough for a numbered marker; these helpers iterate all of them
 * so any customer block can be edited.
 */
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { getEntityHandle, openAs } from "../core/oda";
import { OpenMode } from "../core/constants";
import { isBlockReference } from "../entities/detection";
import { Logger } from "@/logger";
import type { SetBlockAttributeParams } from "../commands/SetBlockAttributeCommand";

/** One editable (non-constant) attribute definition (ATTDEF) of a block. */
export interface BlockAttributeDefInfo {
  /** Attribute tag as authored in the block definition, e.g. "DIRECTION". */
  tag: string;
  /** Prompt authored in the definition — AutoCAD's insert-time question. May be empty. */
  prompt: string;
  /** Default text of the definition. */
  defaultValue: string;
}

/** One attribute of a block reference, flattened for the UI layer. */
export interface BlockAttributeInfo {
  /** Attribute tag as authored in the block definition, e.g. "DIRECTION". */
  tag: string;
  /** Current text value. */
  value: string;
  /** Handle of the OdDbAttribute itself — the stable id used by undo/redo. */
  attrHandle: string;
  /** True when the attribute is hidden in the drawing (AutoCAD "invisible" flag). */
  invisible: boolean;
}

/**
 * List every attribute of a block reference, in definition order.
 * Returns an empty array for non-blocks and blocks without attributes.
 */
export function listBlockAttributes(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): BlockAttributeInfo[] {
  if (!isBlockReference(module, entityId)) return [];

  const result: BlockAttributeInfo[] = [];
  let blockRef: DrawingModule.OdDbBlockReference | null = null;
  let iterator: DrawingModule.OdDbObjectIterator | null = null;

  try {
    blockRef = openAs(entityId, module.OdDbBlockReference, OpenMode.kForRead);
    if (!blockRef) return [];

    iterator = blockRef.attributeIterator();
    for (; !iterator.done(); iterator.step(true, true)) {
      const attrId = iterator.objectId();
      const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForRead);
      if (!attr) {
        deleteObj(attrId);
        continue;
      }
      const odTag = attr.tag();
      const odValue = attr.textString();
      result.push({
        tag: odTag.toString(),
        value: odValue.toString(),
        attrHandle: getEntityHandle(attrId),
        invisible: attr.isInvisible(),
      });
      deleteObj(odTag);
      deleteObj(odValue);
      deleteObj(attr);
      deleteObj(attrId);
    }
  } catch (error) {
    Logger.captureException(error as Error, {
      context: "blocks/attributes.ts : listBlockAttributes",
    });
  } finally {
    deleteObj(iterator);
    deleteObj(blockRef);
  }

  return result;
}

/**
 * Set the text of one attribute, addressed by tag.
 *
 * Mutates the database and returns the params for the undo/redo command, following
 * the convention of photomarker/transform.ts. Returns null when the tag is unknown
 * or the value is unchanged — the caller then skips the command entirely.
 */
export function setBlockAttribute(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  tag: string,
  newValue: string,
): SetBlockAttributeParams | null {
  if (!isBlockReference(module, entityId)) return null;

  const attribute = listBlockAttributes(module, entityId).find(
    (candidate) => candidate.tag === tag,
  );
  if (!attribute || attribute.value === newValue) return null;

  const attrId = findAttributeId(module, entityId, tag);
  if (!attrId) return null;

  let attr: DrawingModule.OdDbAttribute | null = null;
  try {
    attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForWrite);
    if (!attr) return null;

    const odValue = new module.OdString(newValue);
    attr.setTextString(odValue);
    deleteObj(odValue);

    return {
      blockHandle: getEntityHandle(entityId),
      attrHandle: attribute.attrHandle,
      tag,
      oldValue: attribute.value,
      newValue,
    };
  } catch (error) {
    Logger.captureException(error as Error, {
      context: "blocks/attributes.ts : setBlockAttribute",
      tag,
    });
    return null;
  } finally {
    deleteObj(attr);
    deleteObj(attrId);
  }
}

/**
 * Iterate the editable (non-constant) ATTDEFs of an open block definition.
 * The callback must not keep the attDef: it is freed right after the call.
 */
function forEachEditableAttributeDefinition(
  module: DrawingModuleType,
  blockRecord: DrawingModule.OdDbBlockTableRecord,
  callback: (attDef: DrawingModule.OdDbAttributeDefinition) => void,
): void {
  let iter: DrawingModule.OdDbObjectIterator | null = null;
  try {
    iter = blockRecord.newIterator(true, false, false);
    iter.start(true, false);
    while (!iter.done()) {
      const entity = iter.entity(OpenMode.kForRead, false);
      const attDef = module.OdDbAttributeDefinition.cast(entity);
      deleteObj(entity);
      if (attDef) {
        if (!attDef.isConstant()) callback(attDef);
        deleteObj(attDef);
      }
      iter.step(true, false);
    }
  } finally {
    deleteObj(iter);
  }
}

/** Editable ATTDEFs of an open block definition, in definition order. */
export function listAttributeDefinitions(
  module: DrawingModuleType,
  blockRecord: DrawingModule.OdDbBlockTableRecord,
): BlockAttributeDefInfo[] {
  const result: BlockAttributeDefInfo[] = [];
  try {
    forEachEditableAttributeDefinition(module, blockRecord, (attDef) => {
      const odTag = attDef.tag();
      const odPrompt = attDef.prompt();
      const odDefault = attDef.textString();
      result.push({
        tag: odTag.toString(),
        prompt: odPrompt.toString(),
        defaultValue: odDefault.toString(),
      });
      deleteObj(odTag);
      deleteObj(odPrompt);
      deleteObj(odDefault);
    });
  } catch (error) {
    Logger.captureException(error as Error, {
      context: "blocks/attributes.ts : listAttributeDefinitions",
    });
  }
  return result;
}

/**
 * Editable ATTDEFs of a block addressed by name in a database.
 * Returns [] when the block is unknown — callers treat that as "no attributes".
 */
export function listBlockDefinitionAttributes(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  blockName: string,
): BlockAttributeDefInfo[] {
  let blockTable: DrawingModule.OdDbBlockTable | null = null;
  let record: DrawingModule.OdDbSymbolTableRecord | null = null;
  let blockRecord: DrawingModule.OdDbBlockTableRecord | null = null;
  try {
    const blockTableId = db.getBlockTableId();
    blockTable = openAs(blockTableId, module.OdDbBlockTable, OpenMode.kForRead);
    if (!blockTable) return [];

    const nameStr = new module.OdString(blockName);
    const exists = blockTable.has(nameStr);
    if (!exists) {
      deleteObj(nameStr);
      return [];
    }
    record = blockTable.getAt(nameStr, OpenMode.kForRead, false);
    deleteObj(nameStr);

    blockRecord = module.OdDbBlockTableRecord.cast(record);
    if (!blockRecord) return [];

    return listAttributeDefinitions(module, blockRecord);
  } catch (error) {
    Logger.captureException(error as Error, {
      context: "blocks/attributes.ts : listBlockDefinitionAttributes",
    });
    return [];
  } finally {
    deleteObj(blockRecord);
    deleteObj(record);
    deleteObj(blockTable);
  }
}

export interface AppendAttributesOptions {
  /** Text for each created attribute — defaults to the definition's default text. */
  getValue?: (tag: string, defaultValue: string) => string;
  /** Hook run on each attribute after its value is set (e.g. text style overrides). */
  decorate?: (attr: DrawingModule.OdDbAttribute) => void;
}

/**
 * Instantiate the editable ATTDEFs of a block definition on a reference — without
 * this an inserted block renders no attribute text at all.
 *
 * The reference must already be database-resident (appended to a space): ODA's
 * pattern is appendAttribute first, then setAttributeFromBlock1 to copy geometry,
 * style and visibility from the definition and apply the reference's transform.
 *
 * Returns the ids of the created attributes. The caller owns them — undo needs
 * their handles so the whole insert hides/reappears as one.
 */
export function appendAttributesFromDefinitions(
  module: DrawingModuleType,
  blockRef: DrawingModule.OdDbBlockReference,
  blockRecordId: DrawingModule.OdDbObjectId,
  options: AppendAttributesOptions = {},
): DrawingModule.OdDbObjectId[] {
  const created: DrawingModule.OdDbObjectId[] = [];
  let blockRecord: DrawingModule.OdDbBlockTableRecord | null = null;
  let blockXform: DrawingModule.OdGeMatrix3d | null = null;

  try {
    blockRecord = openAs(
      blockRecordId,
      module.OdDbBlockTableRecord,
      OpenMode.kForRead,
    );
    if (!blockRecord) return created;
    blockXform = blockRef.blockTransform();

    forEachEditableAttributeDefinition(module, blockRecord, (attDef) => {
      const attr = module.OdDbAttribute.createObject();
      // Append first — the attribute must be in the database before
      // setAttributeFromBlock1.
      const attrId = blockRef.appendAttribute(attr);
      attr.setAttributeFromBlock1(attDef, blockXform!);

      const odTag = attDef.tag();
      const tag = odTag.toString();
      deleteObj(odTag);
      const odDefault = attDef.textString();
      const defaultValue = odDefault.toString();
      deleteObj(odDefault);

      const value = options.getValue?.(tag, defaultValue) ?? defaultValue;
      const odValue = new module.OdString(value);
      attr.setTextString(odValue);
      deleteObj(odValue);

      options.decorate?.(attr);
      deleteObj(attr);
      if (attrId) created.push(attrId);
    });
  } catch (error) {
    Logger.captureException(error as Error, {
      context: "blocks/attributes.ts : appendAttributesFromDefinitions",
    });
  } finally {
    deleteObj(blockXform);
    deleteObj(blockRecord);
  }

  return created;
}

/** Object id of the attribute carrying `tag`, or null. Caller owns the result. */
function findAttributeId(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  tag: string,
): DrawingModule.OdDbObjectId | null {
  let blockRef: DrawingModule.OdDbBlockReference | null = null;
  let iterator: DrawingModule.OdDbObjectIterator | null = null;
  try {
    blockRef = openAs(entityId, module.OdDbBlockReference, OpenMode.kForRead);
    if (!blockRef) return null;

    iterator = blockRef.attributeIterator();
    for (; !iterator.done(); iterator.step(true, true)) {
      const attrId = iterator.objectId();
      const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForRead);
      if (!attr) {
        deleteObj(attrId);
        continue;
      }
      const odTag = attr.tag();
      const matches = odTag.toString() === tag;
      deleteObj(odTag);
      deleteObj(attr);
      if (matches) return attrId;
      deleteObj(attrId);
    }
    return null;
  } finally {
    deleteObj(iterator);
    deleteObj(blockRef);
  }
}
